import { useState } from 'react';
import { AdOwnerType, CustomOwnerType } from '../../../../../modules/nhi/onPrem/ServiceAccountsTypes';
import { SUBCATEGORIES } from '../../../common/categories';
import * as actions from '../NhiActions';
import {
    Alert,
    Button,
    ButtonStyles,
    Dropdown,
    showAlert,
    SnackAlert,
    TextInput,
    Toggle
} from '@silverfort/sf-components';

const { NHI_ON_PREM } = SUBCATEGORIES;
const SYNC_TYPES_OPTIONS = [
    { title: 'Distinguished name', value: AdOwnerType.GUID },
    { title: 'Email address', value: CustomOwnerType.EMAIL },
    { title: 'Custom ID', value: CustomOwnerType.CUSTOM_TEXT }
];

interface OwnersConfigProps {
    save: () => void;
    fields: any;
    dispatch: React.Dispatch<any>;
}

const OwnersConfig: React.FC<OwnersConfigProps> = ({ save, dispatch, fields }) => {
    const [applied, setApplied] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const ownersConfig: {
        ownerType: CustomOwnerType | AdOwnerType;
        ownerAdKey: string;
        enabled: boolean;
    } = fields[NHI_ON_PREM.ID].fields.ownersConfig.value;

    const applyDisabled = ownersConfig.enabled && (!ownersConfig.ownerAdKey || !ownersConfig.ownerType);

    const onToggleOwnersSync = () => {
        dispatch({
            type: actions.EDIT_OWNERS_SYNC_CONFIG,
            payload: { enabled: !ownersConfig.enabled }
        });
    };

    const onOwnerAdKeyChange = (value: string) => {
        dispatch({
            type: actions.EDIT_OWNERS_SYNC_CONFIG,
            payload: { ownerAdKey: value }
        });
    };

    const onOwnerTypeChange = (value: string | string[] | undefined) => {
        dispatch({
            type: actions.EDIT_OWNERS_SYNC_CONFIG,
            payload: { ownerType: value }
        });
    };

    const onSaveOwnersConfig = async () => {
        try {
            if (!isSaving) {
                setIsSaving(true);
                await save();
                showAlert('Changes take effect after the next full AD sync', 'info', {
                    title: 'Owner sync settings saved'
                });
                setApplied(true);
            }
        } catch (error) {
            console.error('Error saving owners config:', error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <div>
                <div className="text-sm font-medium">Sync ownership from AD</div>
                <div className="text-xs">Select the relevant AD attribute and value to determine ownership</div>
            </div>
            <div className="flex flex-col gap-4 border p-4 rounded-lg">
                <div className="flex gap-4">
                    <div className="flex gap-4 items-center">
                        <div className="text-sf font-medium">Attribute name</div>
                        <TextInput value={ownersConfig.ownerAdKey} onChange={onOwnerAdKeyChange} />
                    </div>
                    <div className="flex gap-4 items-center">
                        <div className="text-sf font-medium">Value type</div>
                        <Dropdown
                            items={SYNC_TYPES_OPTIONS}
                            selectedItems={ownersConfig.ownerType}
                            text="SELECT"
                            onChange={onOwnerTypeChange}
                            variant="trigger"
                        />
                    </div>
                </div>
                <div>
                    <Toggle
                        className="mb-0"
                        onChange={onToggleOwnersSync}
                        checked={ownersConfig.enabled}
                        label="Enable automatic sync"
                    />
                </div>
                <div className="flex items-center">
                    <Button
                        text={isSaving ? 'Saving...' : 'Save'}
                        onClick={onSaveOwnersConfig}
                        buttonStyle={ButtonStyles.Primary}
                        disabled={applyDisabled}
                    />
                </div>
            </div>
            <SnackAlert
                anchorOrigin={{
                    horizontal: 'center',
                    vertical: 'bottom'
                }}
                open={applied}
                onClose={() => setApplied(false)}
                message="Owner sync settings saved"
                autoHideDuration={3000}
            />
            <Alert />
        </div>
    );
};
export default OwnersConfig;
